import type { BaseSliceState } from '../../types'

// The preference docs the client materializes out of /users/{uid}/preferences. Closed set of
// doc ids — callers reference PreferenceDoc.* so a typo can't silently target a phantom doc.
export enum PreferenceDoc {
  MealPlans = 'meal_plans',
  Settings = 'settings',
  Privacy = 'privacy',
  Library = 'library',
  Home = 'home'
}

// preferences/meal_plans — resume state for the Meal Plans page (the last plan the user viewed).
export interface MealPlanPreferences {
  lastViewedPlanId: string | null
}

// preferences/settings — user-set app preferences surfaced on Settings → Preferences.
export interface PreferencesSettings {
  // Default for the per-plan "Pro Level-ups" toggle in the meal-plan request dialog.
  proLevelUps: boolean
  // The browser's IANA zone, mirrored so server-rendered surfaces can date things in the
  // user's own day. Absent until the client has written it once.
  timeZone?: string
  // `shipDate` of the newest release-log entry the user has seen. Absent until seeded on first read.
  whatsNewLastSeen?: string
  // ISO time the user acted on the feedback-button nudge (or clicked the button first). Once set, it never shows.
  feedbackNudgeSeenAt?: string
}

// preferences/privacy — the cross-device mirror of the analytics opt-out. This context owns the
// Firestore read/write; the boot-time source of truth (localStorage) + the live-SDK side effect
// live in AnalyticsConsentContext, which consumes this doc rather than subscribing directly.
export interface PrivacyPreferences {
  analyticsOptOut?: boolean
}

// The library's pinned "Tonight, maybe?" pick. `selectFeatured` scores a pool whose
// membership and ordering both depend on `favorite`, and the pick was a positional index into
// it — so favoriting any meal silently re-rolled the week's suggestion. The pick is stored once
// per ISO week instead of re-derived, which is what actually makes it stable.
export interface FeaturedPin {
  mealId: string
  // ISO-8601 week the pick was made for; `year` is the ISO week-year, not the calendar year.
  year: number
  week: number
}

// preferences/library — pinned state for the Recipes library page.
export interface LibraryPreferences {
  featured?: FeaturedPin | null
}

// preferences/home — what the user has set aside on the home page. Keyed by what was snoozed;
// each value is the ISO time the snooze ends.
export interface HomePreferences {
  snoozes?: Record<string, string>
}

// Maps each preference doc id to its stored shape — the typed contract for updatePreferences.
export interface PreferenceDocShapes {
  [PreferenceDoc.MealPlans]: MealPlanPreferences
  [PreferenceDoc.Settings]: PreferencesSettings
  [PreferenceDoc.Privacy]: PrivacyPreferences
  [PreferenceDoc.Library]: LibraryPreferences
  [PreferenceDoc.Home]: HomePreferences
}

export interface PreferencesState extends BaseSliceState {
  mealPlans: MealPlanPreferences | null
  settings: PreferencesSettings | null
  privacy: PrivacyPreferences | null
  library: LibraryPreferences | null
  home: HomePreferences | null
}

// One snapshot of the whole collection: each known doc resolved to its shape, or null if absent.
export interface PreferencesSnapshot {
  mealPlans: MealPlanPreferences | null
  settings: PreferencesSettings | null
  privacy: PrivacyPreferences | null
  library: LibraryPreferences | null
  home: HomePreferences | null
}

export enum PreferencesActionTypes {
  SET = 'preferences/set',
  SET_ERROR = 'preferences/error',
  CLEAR = 'preferences/clear'
}

export type PreferencesAction =
  | { type: PreferencesActionTypes.SET; payload: PreferencesSnapshot }
  | { type: PreferencesActionTypes.SET_ERROR; payload: string }
  | { type: PreferencesActionTypes.CLEAR }
